import React from "react";
import { Sparkles, ShoppingBag, Landmark, Stethoscope, ArrowRight, MessageCircle } from "lucide-react";
import { BOOK_CALL_URL, WHATSAPP_URL } from "@/lib/constants";

const brands = [
  {
    name: "Nykaa",
    tag: "D2C Beauty",
    icon: Sparkles,
    tint: "#f7dfe5",
    accent: "#c94b7a",
    uses: [
      "Order confirmations",
      "Product recommendations",
      "Back-in-stock alerts",
      "Re-engagement campaigns",
    ],
    why: "Customers don’t forget. They’re reminded at the right time. They come back and buy.",
  },
  {
    name: "Flipkart / Amazon Sellers",
    tag: "E-commerce",
    icon: ShoppingBag,
    tint: "#e4ecf7",
    accent: "#3b6cc0",
    uses: [
      "Cart abandonment recovery",
      "Delivery updates",
      "Cross-sell & upsell",
    ],
    why: "Recover lost sales. Increase average order value.",
  },
  {
    name: "HDFC / ICICI",
    tag: "Banking",
    icon: Landmark,
    tint: "#e6efe8",
    accent: "#2d7a5f",
    uses: ["Payment reminders", "EMI alerts", "Renewal nudges"],
    why: "People don’t act once. They act after reminders.",
  },
  {
    name: "Practo / Clinics",
    tag: "Healthcare",
    icon: Stethoscope,
    tint: "#f5ecdc",
    accent: "#b38438",
    uses: [
      "Appointment confirmations",
      "Reminder sequences",
      "Post-visit follow-ups",
    ],
    why: "Fewer no-shows. More completed appointments.",
  },
];

export default function BrandProof() {
  return (
    <section
      id="proof"
      data-testid="proof-section"
      className="relative py-20 lg:py-28"
      style={{ background: "var(--cream)" }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl">
          <h2
            className="font-display text-4xl sm:text-5xl lg:text-[58px] leading-[1.1]"
            data-testid="proof-headline"
          >
            The biggest brands are{" "}
            <span className="font-display-italic">already doing this.</span>
          </h2>
          <p className="mt-5 text-lg" style={{ color: "var(--text-muted)" }}>
            They don’t rely on one message. They build a system of
            interactions. That’s what you’re missing.
          </p>
        </div>

        <div className="mt-14 grid md:grid-cols-2 gap-5 lg:gap-7">
          {brands.map((b, i) => {
            const Icon = b.icon;
            return (
              <div
                key={b.name}
                data-testid={`proof-card-${i}`}
                className="rounded-3xl p-7 lg:p-9 bg-white card-glow"
                style={{
                  border: "1px solid var(--line)",
                  boxShadow:
                    "0 30px 60px -30px rgba(31,35,48,0.15), 0 2px 6px -2px rgba(31,35,48,0.04)",
                }}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div
                      className="w-12 h-12 rounded-2xl flex items-center justify-center"
                      style={{ background: b.tint }}
                    >
                      <Icon size={22} style={{ color: b.accent }} />
                    </div>
                    <div>
                      <h3 className="font-display text-2xl text-[var(--ink)] leading-tight">
                        {b.name}
                      </h3>
                      <div
                        className="text-xs uppercase tracking-widest mt-0.5"
                        style={{ color: "var(--text-muted)" }}
                      >
                        {b.tag}
                      </div>
                    </div>
                  </div>
                  <span
                    className="text-xs font-display-italic"
                    style={{ color: "var(--gold-dark)" }}
                  >
                    live system
                  </span>
                </div>

                <ul className="mt-6 space-y-2.5">
                  {b.uses.map((u) => (
                    <li key={u} className="flex items-start gap-3">
                      <span
                        className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0"
                        style={{ background: "var(--gold)" }}
                      />
                      <span className="text-[15px] text-[var(--ink)]">{u}</span>
                    </li>
                  ))}
                </ul>

                <div
                  className="mt-6 pt-5 border-t text-sm leading-relaxed"
                  style={{
                    borderColor: "var(--line)",
                    color: "var(--text-muted)",
                  }}
                >
                  <span
                    className="font-display-italic text-base"
                    style={{ color: "var(--ink)" }}
                  >
                    Why it works —{" "}
                  </span>
                  {b.why}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-14 flex flex-wrap justify-center gap-3">
          <a
            href={BOOK_CALL_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="proof-cta"
            className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm sm:text-base font-semibold transition-all hover:scale-[1.03]"
            style={{ background: "var(--gold)", color: "var(--ink)" }}
          >
            Book a Strategy Call
            <ArrowRight
              size={17}
              className="transition-transform group-hover:translate-x-0.5"
            />
          </a>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="proof-whatsapp-cta"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm sm:text-base font-semibold text-white transition-all hover:scale-[1.03]"
            style={{ background: "var(--whatsapp)" }}
          >
            <MessageCircle size={17} strokeWidth={2.5} />
            Chat on WhatsApp
          </a>
        </div>
      </div>
    </section>
  );
}
